import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FiArrowLeft, FiDownload, FiRefreshCw } from 'react-icons/fi';
import reportsService from '../../services/reportsService';

const ReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);

  // Fetch report details
  const { data: report, isLoading: reportLoading } = useQuery({
    queryKey: ['report', id],
    queryFn: () => reportsService.getReportById(id),
  });

  // Generate report mutation
  const generateMutation = useMutation({
    mutationFn: () => reportsService.generateReport(id),
    onSuccess: (data) => {
      setReportData(data);
      toast.success('Report generated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to generate report');
    },
  });

  const handleGenerate = () => {
    generateMutation.mutate();
  };

  const handleExportCSV = () => {
    if (reportData?.data) {
      reportsService.exportToCSV(reportData.data, report?.report?.name || 'report');
      toast.success('Report exported to CSV');
    }
  };

  const handleExportJSON = () => {
    if (reportData?.data) {
      reportsService.exportToJSON(reportData.data, report?.report?.name || 'report');
      toast.success('Report exported to JSON');
    }
  };

  const renderReportData = () => {
    if (!reportData?.data) return null;

    const data = reportData.data;

    // Project Status Report
    if (data.projects) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Projects</p>
              <p className="text-2xl font-bold text-blue-600">{data.summary.total}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Budget</p>
              <p className="text-2xl font-bold text-green-600">${data.summary.total_budget.toLocaleString()}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Spent</p>
              <p className="text-2xl font-bold text-yellow-600">${data.summary.total_spent.toLocaleString()}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Remaining</p>
              <p className="text-2xl font-bold text-purple-600">
                ${(data.summary.total_budget - data.summary.total_spent).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.projects.map((project) => (
                  <tr key={project.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{project.name}</div>
                      <div className="text-sm text-gray-500">{project.code}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {project.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{project.priority}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${project.progress_percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm">{project.progress_percentage}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      ${project.budget_allocated?.toLocaleString() || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Time Tracking Report
    if (data.entries) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Hours</p>
              <p className="text-2xl font-bold text-blue-600">{data.summary.total_hours.toFixed(2)}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Billable Hours</p>
              <p className="text-2xl font-bold text-green-600">{data.summary.billable_hours.toFixed(2)}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Non-Billable</p>
              <p className="text-2xl font-bold text-yellow-600">{data.summary.non_billable_hours.toFixed(2)}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-purple-600">${data.summary.total_revenue.toFixed(2)}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">By Project</h3>
            <div className="space-y-2">
              {data.summary.by_project.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium">{item.project?.name}</span>
                  <span className="text-gray-600">{item.hours.toFixed(2)} hours ({item.entries} entries)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Task Progress Report
    if (data.tasks) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Tasks</p>
              <p className="text-2xl font-bold text-blue-600">{data.summary.total}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{data.summary.completed}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-yellow-600">{data.summary.in_progress}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{data.summary.overdue}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.tasks.slice(0, 20).map((task) => (
                  <tr key={task.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{task.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{task.project?.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {task.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{task.priority}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return <div className="text-gray-600">No data available for this report type.</div>;
  };

  if (reportLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate('/app/reports')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft className="mr-2" />
            Back to Reports
          </button>
          <h1 className="text-3xl font-bold text-gray-900">{report?.report?.name}</h1>
          <p className="mt-1 text-sm text-gray-600">{report?.report?.description}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleGenerate}
            disabled={generateMutation.isLoading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400"
          >
            <FiRefreshCw className={`mr-2 ${generateMutation.isLoading ? 'animate-spin' : ''}`} />
            {generateMutation.isLoading ? 'Generating...' : 'Generate Report'}
          </button>
          {reportData && (
            <>
              <button
                onClick={handleExportCSV}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                <FiDownload className="mr-2" />
                Export CSV
              </button>
              <button
                onClick={handleExportJSON}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <FiDownload className="mr-2" />
                Export JSON
              </button>
            </>
          )}
        </div>
      </div>

      {reportData ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 pb-4 border-b">
            <p className="text-sm text-gray-600">
              Generated: {new Date(reportData.report.generated_at).toLocaleString()}
            </p>
          </div>
          {renderReportData()}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-600 mb-4">Click "Generate Report" to view the data</p>
        </div>
      )}
    </div>
  );
};

export default ReportView;

